
import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { TeacherSubject } from '@/hooks/useTeacherSubjects';
import { SUBJECT_OPTIONS } from '../constants/subjectOptions';
import { EDUCATION_STAGES } from '../constants/educationStages';

interface SubjectFormDialogProps {
  teacherCode: string;
  editingSubject: TeacherSubject | null;
  onSubmit: (data: any) => Promise<boolean>;
  onEditingChange: (subject: TeacherSubject | null) => void;
}

export function SubjectFormDialog({ 
  teacherCode, 
  editingSubject, 
  onSubmit, 
  onEditingChange 
}: SubjectFormDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    grade_level: '',
    course_type: 'Regular' as 'Regular' | 'Inter'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.subject || !formData.grade_level) {
      return;
    }

    const success = await onSubmit({
      teacher_code: teacherCode,
      ...formData
    });

    if (success) {
      resetForm();
      setIsOpen(false);
    }
  };

  const resetForm = () => {
    setFormData({ subject: '', grade_level: '', course_type: 'Regular' });
    onEditingChange(null);
  };

  const handleEdit = (subject: TeacherSubject) => {
    onEditingChange(subject);
    setFormData({
      subject: subject.subject,
      grade_level: subject.grade_level,
      course_type: subject.course_type
    });
    setIsOpen(true);
  };

  // Expose handleEdit through a prop or callback
  React.useEffect(() => {
    if (editingSubject && !isOpen) {
      handleEdit(editingSubject);
    }
  }, [editingSubject]);

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) resetForm();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="w-4 h-4 mr-2" />
          เพิ่มวิชา
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {editingSubject ? 'แก้ไขวิชาที่สอน' : 'เพิ่มวิชาที่สอน'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="subject">วิชา *</Label>
            <Select
              value={formData.subject}
              onValueChange={(value) => setFormData(prev => ({ ...prev, subject: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="เลือกวิชา" />
              </SelectTrigger>
              <SelectContent>
                {SUBJECT_OPTIONS.map((subject) => (
                  <SelectItem key={subject} value={subject}>
                    {subject}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="grade_level">ระดับชั้น *</Label>
            <Select
              value={formData.grade_level}
              onValueChange={(value) => setFormData(prev => ({ ...prev, grade_level: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="เลือกระดับชั้น" />
              </SelectTrigger>
              <SelectContent>
                {EDUCATION_STAGES.map((stage) => (
                  <SelectItem key={stage} value={stage}>
                    {stage}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>หลักสูตร *</Label>
            <RadioGroup
              value={formData.course_type}
              onValueChange={(value: 'Regular' | 'Inter') => 
                setFormData(prev => ({ ...prev, course_type: value }))
              }
              className="space-y-2 mt-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Regular" id="regular" />
                <Label htmlFor="regular" className="cursor-pointer">ปกติ</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Inter" id="inter" />
                <Label htmlFor="inter" className="cursor-pointer">Inter</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsOpen(false);
                resetForm();
              }}
              className="flex-1"
            >
              ยกเลิก
            </Button>
            <Button type="submit" className="flex-1">
              {editingSubject ? 'อัพเดท' : 'เพิ่ม'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
